import { createTranslationHook } from '../i18nFactory';
import { TEXTO_COMUN_ES, TEXTO_COMUN_EN } from '../commonTexts';
import type { TranslateFn } from '../i18nFactory';

/**
 * i18n del widget de Retenciones (FEAT-105-11).
 *
 * Usa `useTranslation` de siesa-ui-kit vía `createTranslationHook`; si el host no
 * registró las claves `withholdings.*` (el motor devuelve la propia clave), cae al
 * diccionario local del idioma activo y, en último término, al español. Garantiza
 * español listo para usar sin depender de que el documento consumidor registre los
 * recursos. Las claves siguen el formato plano que espera `DefaultTranslationEngine`.
 */

export const WITHHOLDINGS_TRANSLATIONS_ES: Record<string, string> = {
  'withholdings.panel.title': 'Retenciones',
  'withholdings.panel.empty': 'Este documento no tiene retenciones.',
  // Sin uso: el panel muestra un skeleton en vez de texto. No se borra sin
  // confirmar con diseño que el skeleton es definitivo.
  // Deuda con dueño en docs/i18n-masterdocument-deuda.md (punto 2).
  'withholdings.panel.loading': 'Cargando retenciones…',
  'withholdings.panel.loadError': 'No se pudieron cargar las retenciones.',

  'withholdings.column.class': 'Clase',
  'withholdings.column.base': 'Base',
  'withholdings.column.rate': 'Tasa',
  'withholdings.column.total': 'Total',
  'withholdings.column.action': 'Acción',
  'withholdings.column.origin': 'Origen',

  'withholdings.origin.manual': 'Manual',
  'withholdings.origin.manipulated': 'Modificado',
  'withholdings.origin.automatic': 'Automático',
  'withholdings.origin.originalTooltip': 'Valor original: {value}',

  'withholdings.action.1': 'Aumenta total',
  'withholdings.action.2': 'Disminuye total',
  'withholdings.action.3': 'Sin efecto en total',
  'withholdings.action.4': 'Sin contabilizar',
  // Guarda para valores que el backend pueda añadir fuera del rango 1..4: sin ella
  // la celda renderizaba la clave cruda `withholdings.action.7`.
  'withholdings.action.unknown': 'Acción no reconocida',

  'withholdings.actions.add': TEXTO_COMUN_ES.add,
  'withholdings.actions.edit': 'Modificar',
  'withholdings.actions.delete': TEXTO_COMUN_ES.delete,
  'withholdings.actions.recalculate': 'Recalcular',

  'withholdings.add.title': 'Agregar retención',
  'withholdings.add.class': 'Clase de retención',
  'withholdings.add.classPlaceholder': 'Seleccione una clase',
  'withholdings.add.key': 'Clave de retención',
  'withholdings.add.keyPlaceholder': 'Seleccione una clave',
  'withholdings.add.operationCenter': 'Centro de operación',
  'withholdings.add.operationCenterPlaceholder': 'Seleccione un centro',
  'withholdings.add.base': 'Base',
  'withholdings.add.total': 'Valor retenido',
  'withholdings.add.submit': TEXTO_COMUN_ES.add,
  'withholdings.add.cancel': TEXTO_COMUN_ES.cancel,

  'withholdings.edit.title': 'Modificar retención',
  'withholdings.edit.base': 'Base',
  'withholdings.edit.total': 'Valor retenido',
  'withholdings.edit.action': 'Acción sobre el total',
  'withholdings.edit.submit': TEXTO_COMUN_ES.save,
  'withholdings.edit.cancel': TEXTO_COMUN_ES.cancel,

  'withholdings.delete.title': 'Eliminar retención',
  'withholdings.delete.body': 'Se eliminará esta retención manual. ¿Continuar?',
  'withholdings.delete.confirm': TEXTO_COMUN_ES.delete,
  'withholdings.delete.cancel': TEXTO_COMUN_ES.cancel,

  'withholdings.recalculate.title': 'Recalcular retenciones',
  'withholdings.recalculate.body':
    'Se regenerarán las retenciones automáticas. Las manuales y las modificadas se preservan. ¿Continuar?',
  'withholdings.recalculate.confirm': 'Recalcular',
  'withholdings.recalculate.cancel': TEXTO_COMUN_ES.cancel,

  'withholdings.validation.required': 'Campo requerido.',
  'withholdings.validation.basePositive': 'La base debe ser mayor que 0.',
  'withholdings.validation.totalPositive': 'El valor debe ser mayor que 0.',
  'withholdings.validation.operationCenterRequired':
    'El centro de operación es obligatorio para claves municipales.',

  'withholdings.error.forbidden': TEXTO_COMUN_ES.errorForbidden,
  'withholdings.error.conflict': 'Conflicto: la retención cambió o ya existe esa combinación.',
  // ⚠️ Estas tres siguen sin conectar, a propósito y no por descuido.
  // `reportError` discrimina por `status` HTTP, y las tres son sub-casos de
  // negocio del MISMO código (409/422): "modificada por otro usuario",
  // "solo manuales" y "no es borrador" son indistinguibles sin un `errorCode`.
  // `WithholdingsHttpError` solo declara `status` y `detail`, así que conectarlas
  // exigiría (a) extender el tipo con el `errorCode` del backend y (b) conocer
  // sus valores reales — que no están en este repo. Inventarlos daría un mapeo
  // que nunca se dispara y aparentaría cobertura.
  // Mientras tanto el usuario ve el `detail` del backend, que llega traducido.
  // Registrada con dueño en docs/i18n-masterdocument-deuda.md (punto 1).
  'withholdings.error.concurrency':
    'La retención fue modificada por otro usuario. Se recargó la lista.',
  'withholdings.error.cannotDeleteAutomatic': 'Solo se pueden eliminar retenciones manuales.',
  'withholdings.error.notDraft': 'El documento no está en estado borrador.',
  'withholdings.error.validation': 'Revise los datos ingresados.',
  'withholdings.error.notFound': 'Recurso no encontrado.',
  'withholdings.error.liquitaxUnavailable': 'El servicio de liquidación no está disponible.',
  'withholdings.error.generic': TEXTO_COMUN_ES.errorGeneric,
  'withholdings.error.catalog': 'No se pudieron cargar los catálogos.',

  'withholdings.toast.added': 'Retención agregada correctamente.',
  'withholdings.toast.updated': 'Retención actualizada correctamente.',
  'withholdings.toast.deleted': 'Retención eliminada correctamente.',
  'withholdings.toast.recalculated': 'Retenciones recalculadas: {preserved} preservadas, {recalculated} regeneradas.',
};

export const WITHHOLDINGS_TRANSLATIONS_EN: Record<string, string> = {
  'withholdings.panel.title': 'Withholdings',
  'withholdings.panel.empty': 'This document has no withholdings.',
  'withholdings.panel.loading': 'Loading withholdings…',
  'withholdings.panel.loadError': 'Could not load withholdings.',

  'withholdings.column.class': 'Class',
  'withholdings.column.base': 'Base',
  'withholdings.column.rate': 'Rate',
  'withholdings.column.total': 'Total',
  'withholdings.column.action': 'Action',
  'withholdings.column.origin': 'Origin',

  'withholdings.origin.manual': 'Manual',
  'withholdings.origin.manipulated': 'Modified',
  'withholdings.origin.automatic': 'Automatic',
  'withholdings.origin.originalTooltip': 'Original value: {value}',

  'withholdings.action.1': 'Increases total',
  'withholdings.action.2': 'Decreases total',
  'withholdings.action.3': 'No effect on total',
  'withholdings.action.4': 'Not accounted',
  'withholdings.action.unknown': 'Unrecognized action',

  'withholdings.actions.add': TEXTO_COMUN_EN.add,
  'withholdings.actions.edit': 'Edit',
  'withholdings.actions.delete': TEXTO_COMUN_EN.delete,
  'withholdings.actions.recalculate': 'Recalculate',

  'withholdings.add.title': 'Add withholding',
  'withholdings.add.class': 'Withholding class',
  'withholdings.add.classPlaceholder': 'Select a class',
  'withholdings.add.key': 'Withholding key',
  'withholdings.add.keyPlaceholder': 'Select a key',
  'withholdings.add.operationCenter': 'Operation center',
  'withholdings.add.operationCenterPlaceholder': 'Select a center',
  'withholdings.add.base': 'Base',
  'withholdings.add.total': 'Withheld value',
  'withholdings.add.submit': TEXTO_COMUN_EN.add,
  'withholdings.add.cancel': TEXTO_COMUN_EN.cancel,

  'withholdings.edit.title': 'Edit withholding',
  'withholdings.edit.base': 'Base',
  'withholdings.edit.total': 'Withheld value',
  'withholdings.edit.action': 'Action on total',
  'withholdings.edit.submit': TEXTO_COMUN_EN.save,
  'withholdings.edit.cancel': TEXTO_COMUN_EN.cancel,

  'withholdings.delete.title': 'Delete withholding',
  'withholdings.delete.body': 'This manual withholding will be deleted. Continue?',
  'withholdings.delete.confirm': TEXTO_COMUN_EN.delete,
  'withholdings.delete.cancel': TEXTO_COMUN_EN.cancel,

  'withholdings.recalculate.title': 'Recalculate withholdings',
  'withholdings.recalculate.body':
    'Automatic withholdings will be regenerated. Manual and modified ones are preserved. Continue?',
  'withholdings.recalculate.confirm': 'Recalculate',
  'withholdings.recalculate.cancel': TEXTO_COMUN_EN.cancel,

  'withholdings.validation.required': 'Required field.',
  'withholdings.validation.basePositive': 'The base must be greater than 0.',
  'withholdings.validation.totalPositive': 'The value must be greater than 0.',
  'withholdings.validation.operationCenterRequired':
    'The operation center is required for municipal keys.',

  'withholdings.error.forbidden': TEXTO_COMUN_EN.errorForbidden,
  'withholdings.error.conflict': 'Conflict: the withholding changed or that combination already exists.',
  'withholdings.error.concurrency': 'The withholding was modified by another user. The list was reloaded.',
  'withholdings.error.cannotDeleteAutomatic': 'Only manual withholdings can be deleted.',
  'withholdings.error.notDraft': 'The document is not in draft state.',
  'withholdings.error.validation': 'Check the entered data.',
  'withholdings.error.notFound': 'Resource not found.',
  'withholdings.error.liquitaxUnavailable': 'The settlement service is unavailable.',
  'withholdings.error.generic': TEXTO_COMUN_EN.errorGeneric,
  'withholdings.error.catalog': 'Could not load catalogs.',

  'withholdings.toast.added': 'Withholding added successfully.',
  'withholdings.toast.updated': 'Withholding updated successfully.',
  'withholdings.toast.deleted': 'Withholding deleted successfully.',
  'withholdings.toast.recalculated': 'Withholdings recalculated: {preserved} preserved, {recalculated} regenerated.',
};

/** Diccionarios de respaldo por idioma, listos para registrar en el host si se desea. */
export const WITHHOLDINGS_TRANSLATIONS = {
  es: WITHHOLDINGS_TRANSLATIONS_ES,
  en: WITHHOLDINGS_TRANSLATIONS_EN,
} as const;

/** Función de traducción con interpolación `{var}`. */
export type WithholdingsT = TranslateFn;

/**
 * Hook de traducción del widget.
 *
 * Usa `useTranslation` del kit; si la clave no está registrada en el host
 * (el motor devuelve la propia clave), cae al diccionario local del idioma activo.
 */
export const useWithholdingsTranslation = createTranslationHook(WITHHOLDINGS_TRANSLATIONS);
